import { computed, ref, type ComputedRef, type Ref } from 'vue';

import { http } from '@/interceptors/axiosRequestInterceptor';
import type { MyReadingFeedback } from '@/composables/useReadingFeedback';

/**
 * The Needs Attention queue (AB#21587).
 *
 * The proactive counterpart to Ask ELSA: instead of opening an order and asking
 * how it's doing, this is the ranked list of the orders that need the person
 * today, each with the one or two actions that address it. The queue is
 * computed and scoped entirely server-side (NeedsAttentionService), so this
 * layer only fetches, holds and mutates — it makes no ranking or access
 * decisions of its own.
 *
 * Every response uses the app envelope { success, message, result }. A failed
 * load leaves the last good queue in place and records an error rather than
 * blanking the screen, because a queue that vanishes on a hiccup is worse than
 * a slightly stale one.
 */

export type AttentionBand = 'critical' | 'high' | 'medium';

export type AttentionAction =
  | 'assign_factory'
  | 'reply_factory'
  | 'set_ship_date'
  | 'get_tracking'
  | 'update_customer'
  | 'fix_order_type'
  | 'set_in_hand_date'
  | 'open_elsa'
  // AB#21997 — finance cards.
  | 'open_invoices'
  | 'open_order';

/** AB#21997 — the two queues an order's problems are split between. */
export type AttentionTeam = 'order_admin' | 'finance';
/** Which queue a composable instance reads: the viewer's own, or the Financial Controllers'. */
export type AttentionQueue = 'own' | 'finance';

export interface AttentionCta {
  label: string;
  action: AttentionAction | string;
  /** AB#21732 — Y-m-d for a date action (set_in_hand_date / set_ship_date). */
  date?: string | null;
}

// AB#21985 — the three levels (delayed, at risk, keep an eye) live in
// utils/attentionLevels, free of this file's HTTP client so pages and tests can
// use them without it. Re-exported here for the one import most callers have.
export {
  ATTENTION_LEVELS,
  LEVEL_LABEL,
  countLevels,
  emptyLevels,
  levelOf,
  type AttentionLevel,
  type LevelCounts,
} from '@/utils/attentionLevels';
import {
  countLevels,
  emptyLevels,
  levelOf,
  readLevels,
  type AttentionLevel,
  type LevelCounts,
} from '@/utils/attentionLevels';

export interface AttentionItem {
  order_item_id: number;
  order_id: number;
  /** Null until the order is confirmed and numbered (AB#21886). */
  order_number: string | null;
  /** "#28100", or "Order number pending" — what the card prints. */
  order_label?: string;
  company_name: string | null;
  customer_name: string | null;
  problem: string;
  severity: number;
  band: AttentionBand;
  headline: string;
  detail: string | null;
  cta: AttentionCta;
  secondary: AttentionCta | null;
  /** Signed: positive = days remaining, 0 = today, negative = days overdue, null = no date. */
  deadline_days: number | null;
  analysis_status: 'green' | 'orange' | 'red' | null;
  is_stale: boolean;
  order_administrator_id: number | null;
  /** The order's production status, for the stage indicator. Absent on older backends. */
  order_status?: string | null;
  /** True exactly when the level is delayed (AB#21985). Absent on older backends. */
  has_risk?: boolean;
  /** AB#21985 — delayed | at_risk | watch. Absent on older backends: use levelOf(). */
  level?: AttentionLevel;
  level_label?: string;
  /** AB#21997 — which queue the card belongs to. Absent on older backends (order admin). */
  team?: AttentionTeam;
  /** The same order's card in the other queue, when it has one. */
  also?: { team: AttentionTeam; headline: string } | null;
  company_id?: number | null;
  /** Finance cards: how long the issue has stood, and the open amount it is about. */
  age_days?: number | null;
  amount?: number | null;
  currency?: string | null;
}

/** AB#21997 — the Finance team overview (needs-attention/finance-team). */
export interface FinanceIssueRow {
  problem: string;
  label: string;
  orders: number;
  levels: LevelCounts;
  oldest_days: number | null;
}
export interface FinanceOverview {
  total: number;
  levels: LevelCounts;
  by_issue: FinanceIssueRow[];
  overdue_amounts: { currency: string; amount: number; orders: number }[];
  not_invoiced: { count: number; oldest_days: number | null };
  by_merchant: {
    company_id: number | null;
    company_name: string | null;
    orders: number;
    oldest_days: number | null;
  }[];
  by_controller: {
    user_id: number;
    name: string;
    /** Orders invoiced in the last 7 days, from the audit log (absent on an older backend). */
    invoiced_7d?: number;
    actioned_7d: number;
    set_aside: number;
  }[];
  top: AttentionItem[];
}

export interface TeamAdminLoad {
  admin_id: number;
  admin_name: string;
  /** Orders of this admin's that need attention today — NOT their total open load. */
  open_count: number;
  /** The delayed (red) ones. Kept under its old name; equals delayed_count. */
  risk_count: number;
  /** AB#21985 — the three levels. Absent on older backends. */
  delayed_count?: number;
  at_risk_count?: number;
  watch_count?: number;
  /** AB#21903 — ELSA suggestions this admin acted on in the last 7 days. */
  actioned_7d?: number;
}

export interface TeamRollup {
  per_admin: TeamAdminLoad[];
  unassigned: { count: number; items: AttentionItem[]; levels: LevelCounts | null };
  top_risks: AttentionItem[];
  total: number;
  /** AB#21985 — the whole team's queue by level; null on an older backend. */
  levels: LevelCounts | null;
}

interface QueuePayload {
  items?: AttentionItem[];
  count?: number;
  /** AB#21985 — the whole queue (the list may be cut to a page), by level. */
  total?: number;
  levels?: LevelCounts;
  /** AB#21909 — this person's verdict on the current reading, per item. */
  reading_feedback?: Record<string, MyReadingFeedback>;
}

export interface UseNeedsAttention {
  items: Ref<AttentionItem[]>;
  readingFeedback: Ref<Record<string, MyReadingFeedback>>;
  count: Ref<number>;
  /** AB#21985 — the whole queue by level, for the headline, the chips and the badge. */
  levels: Ref<LevelCounts>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  hasItems: ComputedRef<boolean>;
  load: () => Promise<void>;
  refreshCount: () => Promise<void>;
  snooze: (orderItemId: number, snoozedUntil: string, reason?: string) => Promise<boolean>;
  dismiss: (orderItemId: number, reason?: string) => Promise<boolean>;
  restore: (orderItemId: number) => Promise<boolean>;
  loadTeam: () => Promise<TeamRollup | null>;
  loadFinanceTeam: () => Promise<FinanceOverview | null>;
}

/**
 * @param queue AB#21997 — 'own' (default) is the viewer's queue; 'finance' is the
 * Financial Controllers' queue, the same shape from its own endpoints. Setting a
 * card aside uses the same routes for both.
 */
export const useNeedsAttention = (queue: AttentionQueue = 'own'): UseNeedsAttention => {
  const base = queue === 'finance' ? 'needs-attention/finance' : 'needs-attention';
  // AB#21998 — tells the server a set-aside came from the finance queue, so it
  // is tracked there and not as an order admin's snooze.
  const queueTag = queue === 'finance' ? { queue: 'finance' } : {};
  const items = ref<AttentionItem[]>([]);
  const readingFeedback = ref<Record<string, MyReadingFeedback>>({});
  const count = ref(0);
  const levels = ref<LevelCounts>(emptyLevels());
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const hasItems = computed(() => items.value.length > 0);

  const load = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    try {
      // The most the endpoint returns; the level counts cover the whole queue either way.
      const { data } = await http.get(base, { params: { limit: 200 } });
      const result = (data?.result ?? {}) as QueuePayload;

      if (data?.success === true) {
        items.value = Array.isArray(result.items) ? result.items : [];
        // `total` is the whole queue; `count` is the page. Older backends send only count.
        count.value =
          typeof result.total === 'number'
            ? result.total
            : typeof result.count === 'number'
              ? result.count
              : items.value.length;
        levels.value = readLevels(result.levels) ?? countLevels(items.value);
        readingFeedback.value =
          result.reading_feedback && typeof result.reading_feedback === 'object'
            ? result.reading_feedback
            : {};
      } else {
        // Keep the last good queue; just surface the message.
        error.value = data?.message || 'The queue could not be loaded.';
      }
    } catch {
      error.value = 'The queue could not be loaded.';
    } finally {
      isLoading.value = false;
    }
  };

  /** Just the badge number — cheap enough to poll. Never throws to the caller. */
  const refreshCount = async (): Promise<void> => {
    try {
      const { data } = await http.get(`${base}/count`);
      const result = (data?.result ?? {}) as { count?: number; levels?: LevelCounts };
      if (typeof result.count === 'number') {
        count.value = result.count;
      }
      const byLevel = readLevels(result.levels);
      if (byLevel) levels.value = byLevel;
    } catch {
      // A badge that can't refresh keeps its last number rather than clearing.
    }
  };

  /**
   * After a set-aside/restore, drop or keep the row locally so the list reacts
   * immediately, and reconcile the badge with the server. Optimistic on the
   * list, authoritative on the count.
   */
  const removeLocally = (orderItemId: number): void => {
    const gone = items.value.find((i) => i.order_item_id === orderItemId);
    items.value = items.value.filter((i) => i.order_item_id !== orderItemId);
    // Optimistic: one fewer, at that card's level. refreshCount() then reconciles.
    count.value = Math.max(0, count.value - (gone ? 1 : 0));
    if (gone) {
      const lv = levelOf(gone);
      levels.value = { ...levels.value, [lv]: Math.max(0, levels.value[lv] - 1) };
    }
  };

  const snooze = async (
    orderItemId: number,
    snoozedUntil: string,
    reason?: string
  ): Promise<boolean> => {
    try {
      await http.post(`needs-attention/${orderItemId}/snooze`, {
        snoozed_until: snoozedUntil,
        reason: reason ?? null,
        ...queueTag,
      });
      removeLocally(orderItemId);
      void refreshCount();
      return true;
    } catch {
      error.value = 'Could not snooze that item.';
      return false;
    }
  };

  const dismiss = async (orderItemId: number, reason?: string): Promise<boolean> => {
    try {
      await http.post(`needs-attention/${orderItemId}/dismiss`, {
        reason: reason ?? null,
        ...queueTag,
      });
      removeLocally(orderItemId);
      void refreshCount();
      return true;
    } catch {
      error.value = 'Could not dismiss that item.';
      return false;
    }
  };

  const restore = async (orderItemId: number): Promise<boolean> => {
    try {
      await http.post(`needs-attention/${orderItemId}/restore`);
      // A restore is best reflected by reloading — the item's rank decides where
      // it lands, which the client cannot know.
      await load();
      return true;
    } catch {
      error.value = 'Could not restore that item.';
      return false;
    }
  };

  const loadTeam = async (): Promise<TeamRollup | null> => {
    try {
      const { data } = await http.get('needs-attention/team');
      if (data?.success !== true) {
        error.value = data?.message || 'The team overview could not be loaded.';
        return null;
      }
      const r = (data?.result ?? {}) as Partial<TeamRollup>;
      // Deploy-window shim: a backend that predates the risk-count change sends
      // `critical_count` (critical band only). Read it as `risk_count` so the
      // label is right the moment the SPA ships, and the number widens to the
      // real risk count once the backend follows.
      const perAdmin = (Array.isArray(r.per_admin) ? r.per_admin : []).map((row) => ({
        ...row,
        risk_count:
          row.risk_count ?? (row as unknown as { critical_count?: number }).critical_count ?? 0,
      }));
      return {
        per_admin: perAdmin,
        unassigned: {
          count: r.unassigned?.count ?? 0,
          items: Array.isArray(r.unassigned?.items) ? (r.unassigned?.items as AttentionItem[]) : [],
          levels: readLevels(r.unassigned?.levels),
        },
        top_risks: Array.isArray(r.top_risks) ? r.top_risks : [],
        total: typeof r.total === 'number' ? r.total : 0,
        levels: readLevels(r.levels),
      };
    } catch {
      error.value = 'The team overview could not be loaded.';
      return null;
    }
  };

  const loadFinanceTeam = async (): Promise<FinanceOverview | null> => {
    try {
      const { data } = await http.get('needs-attention/finance-team');
      if (data?.success !== true) {
        error.value = data?.message || 'The finance overview could not be loaded.';
        return null;
      }
      const r = (data?.result ?? {}) as Partial<FinanceOverview>;
      const list = <T>(v: T[] | undefined): T[] => (Array.isArray(v) ? v : []);
      return {
        total: typeof r.total === 'number' ? r.total : 0,
        levels: readLevels(r.levels) ?? emptyLevels(),
        by_issue: list(r.by_issue).map((row) => ({
          ...row,
          levels: readLevels(row.levels) ?? emptyLevels(),
        })),
        overdue_amounts: list(r.overdue_amounts),
        not_invoiced: {
          count: r.not_invoiced?.count ?? 0,
          oldest_days: r.not_invoiced?.oldest_days ?? null,
        },
        by_merchant: list(r.by_merchant),
        by_controller: list(r.by_controller),
        top: list(r.top),
      };
    } catch {
      error.value = 'The finance overview could not be loaded.';
      return null;
    }
  };

  return {
    items,
    readingFeedback,
    count,
    levels,
    isLoading,
    error,
    hasItems,
    load,
    refreshCount,
    snooze,
    dismiss,
    restore,
    loadTeam,
    loadFinanceTeam,
  };
};
